// ============================================================
// TMR Portal Admin — Empresas › Solicitações (rota /empresas/solicitacoes) (28 §9)
// ============================================================
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatusBadge } from '../../components/primitives';
import { FilterTabs, PageHeader, SearchBox, Table, Td } from '../../components/ui';
import { Pagination } from '../../components/Pagination';
import { EmptyState, ErrorState, Skeleton } from '../../components/states';
import { maskCNPJ } from '../../lib/format';
import { PERMISSIONS, usePermission } from '../../data/PermissionsProvider';
import { APPLICATION_PAGE_SIZE, useCompanyApplications } from '../../data/companyApplications/queries';
import {
  APPLICATION_STATUS_BADGE,
  APPLICATION_STATUS_OPTIONS,
  applicationModeLabel,
  duplicateLabel,
  formatMoment,
  specialtyLabel,
} from '../../data/companyApplications/status';
import type { CompanyApplicationStatus } from '../../data/companyApplications/contract';
import { useDebounced } from '../../useDebounced';

type StatusTab = CompanyApplicationStatus | 'all';

const TABS: { value: StatusTab; label: string }[] = [
  ...APPLICATION_STATUS_OPTIONS.map((option) => ({ value: option.value as StatusTab, label: option.label })),
  { value: 'all', label: 'Todas' },
];

/**
 * The queue of clinics that asked to join through the public site, newest first. It opens on "Recebidas" —
 * what nobody has picked up yet — and a row opens the application, where it is taken, rejected or approved.
 */
export function CompanyApplicationsPage() {
  const navigate = useNavigate();
  const canRead = usePermission(PERMISSIONS.companyApplicationsRead);
  const [status, setStatus] = useState<StatusTab>('Submitted');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounced(search);

  const list = useCompanyApplications(
    {
      status: status === 'all' ? undefined : status,
      search: debouncedSearch.trim() || undefined,
      page,
    },
    canRead,
  );

  if (!canRead) {
    return (
      <div className="pa-screen" style={{ padding: 24 }}>
        <div className="pa-card">
          <EmptyState
            message="Você não tem permissão para ver as solicitações de cadastro."
            hint="Peça o acesso a quem administra os papéis."
          />
        </div>
      </div>
    );
  }

  const items = list.data?.items ?? [];
  const totalCount = list.data?.totalCount ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalCount / (list.data?.pageSize ?? APPLICATION_PAGE_SIZE)));

  return (
    <div className="pa-screen" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader eyebrow="Empresas" title="Solicitações" desc="Clínicas que pediram o cadastro pelo site." />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <FilterTabs
          tabs={TABS}
          value={status}
          onChange={(value) => {
            setStatus(value as StatusTab);
            setPage(1);
          }}
        />
        <SearchBox
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          placeholder="Protocolo ou CNPJ"
        />
      </div>

      {list.isPending ? (
        <Skeleton rows={5} />
      ) : list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : items.length === 0 ? (
        <div className="pa-card">
          <EmptyState
            message={debouncedSearch ? 'Nenhuma solicitação com este protocolo ou CNPJ.' : 'Nenhuma solicitação aqui.'}
            hint={debouncedSearch ? 'Confira o número completo, como TMR-7KQ2MX.' : 'Quando uma clínica pedir o cadastro pelo site, ela aparece aqui.'}
          />
        </div>
      ) : (
        <Table
          columns={[
            { label: 'Protocolo' },
            { label: 'Clínica' },
            { label: 'CNPJ' },
            { label: 'Modo' },
            { label: 'Recebida em' },
            { label: 'Situação' },
          ]}
        >
          {items.map((item) => {
            const duplicate = duplicateLabel(item);
            return (
              <tr
                key={item.id}
                className="pa-row"
                onClick={() => navigate(`/empresas/solicitacoes/${item.id}`)}
                style={{ cursor: 'pointer' }}
              >
                <Td>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>{item.protocol}</span>
                </Td>
                <Td>
                  <span style={{ fontWeight: 600 }}>{item.tradeName}</span>
                  <span style={{ display: 'block', fontSize: 12.5, color: 'var(--fg-subtle)' }}>
                    {specialtyLabel(item.specialty)} · {item.contactName}
                  </span>
                </Td>
                <Td>
                  {maskCNPJ(item.cnpj)}
                  {duplicate && (
                    <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: 'var(--danger)' }}>{duplicate}</span>
                  )}
                </Td>
                <Td>{applicationModeLabel(item.mode)}</Td>
                <Td>{formatMoment(item.submittedAt)}</Td>
                <Td>
                  <StatusBadge status={APPLICATION_STATUS_BADGE[item.status] ?? item.status} />
                  {item.reviewer && item.status === 'InReview' && (
                    <span style={{ display: 'block', fontSize: 12.5, color: 'var(--fg-subtle)', marginTop: 3 }}>
                      com {item.reviewer.name}
                    </span>
                  )}
                </Td>
              </tr>
            );
          })}
        </Table>
      )}

      {items.length > 0 && <Pagination page={page} pageCount={pageCount} total={totalCount} onChange={setPage} />}
    </div>
  );
}
